import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { Plug, RefreshCw, Search, X, ChevronDown, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

import { DashboardLayout } from '../components/DashboardLayout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useAuth } from '../contexts/AuthContext';
import { API } from '../utils/apiConfig';

const STATUS_META = {
  success:  { label: 'Success',  pill: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  rejected: { label: 'Rejected', pill: 'bg-amber-100  text-amber-700  border-amber-200' },
  error:    { label: 'Error',    pill: 'bg-rose-100   text-rose-700   border-rose-200' },
};

const DAYS_OPTIONS = [
  { value: 7,   label: 'Last 7 days' },
  { value: 30,  label: 'Last 30 days' },
  { value: 90,  label: 'Last 90 days' },
  { value: 0,   label: 'All time' },
];

const formatAt = (iso) => {
  if (!iso) return '—';
  try { return format(new Date(iso), 'dd-MMM HH:mm:ss'); } catch { return iso; }
};

const startDateFromDays = (days) => {
  if (!days) return '';
  const d = new Date();
  d.setDate(d.getDate() - days);
  return format(d, 'yyyy-MM-dd');
};

export default function PublicApiLogsPage() {
  const { token } = useAuth();

  const [days, setDays] = useState(30);
  const [statusFilter, setStatusFilter] = useState('all');
  const [endpointFilter, setEndpointFilter] = useState('all');
  const [partnerFilter, setPartnerFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const fetchLogs = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const params = { status: statusFilter, endpoint: endpointFilter, partner: partnerFilter, limit: 300 };
      const sd = startDateFromDays(days);
      if (sd) params.start_date = sd;
      if (search.trim()) params.search = search.trim();
      const res = await axios.get(`${API}/public-api-logs`, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (err) {
      console.error('Public API logs fetch error', err);
      toast.error('Failed to load public API logs');
    } finally {
      setLoading(false);
    }
  }, [days, statusFilter, endpointFilter, partnerFilter, search, token]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const summary = data?.summary || { total: 0, success: 0, rejected: 0, error: 0 };
  const rows = data?.logs || [];
  const partners = data?.partners || [];
  const loggingEnabled = data?.logging_enabled ?? true;

  return (
    <DashboardLayout>
      <div className="space-y-5" data-testid="public-api-logs-page">
        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-heading font-bold text-2xl sm:text-3xl text-slate-900 tracking-tight flex items-center gap-2">
              <Plug className="w-6 h-6 sm:w-7 sm:h-7 text-primary" />
              Public API Logs
            </h1>
            <p className="text-xs sm:text-sm text-slate-500 mt-1">
              Every partner API booking & cancellation — successful and rejected. Logging can be turned off in Settings → API &amp; Integration Logs.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${
                loggingEnabled
                  ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                  : 'bg-slate-100 text-slate-500 border-slate-200'
              }`}
              data-testid="public-api-logging-status"
            >
              <span className={`w-1.5 h-1.5 rounded-full ${loggingEnabled ? 'bg-emerald-500' : 'bg-slate-400'}`} />
              {loggingEnabled ? 'Logging On' : 'Logging Off'}
            </span>
            <Button onClick={fetchLogs} disabled={loading} variant="outline" data-testid="public-api-logs-refresh-btn">
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Card className="p-3 sm:p-4" data-testid="public-api-summary-total">
            <p className="text-[10px] sm:text-xs uppercase tracking-wider text-slate-500">Total</p>
            <p className="text-xl sm:text-2xl font-bold text-slate-900 mt-1">{summary.total}</p>
          </Card>
          <Card className="p-3 sm:p-4 border-emerald-200 bg-emerald-50/40" data-testid="public-api-summary-success">
            <p className="text-[10px] sm:text-xs uppercase tracking-wider text-emerald-700">Success</p>
            <p className="text-xl sm:text-2xl font-bold text-emerald-700 mt-1">{summary.success}</p>
          </Card>
          <Card className="p-3 sm:p-4 border-amber-200 bg-amber-50/40" data-testid="public-api-summary-rejected">
            <p className="text-[10px] sm:text-xs uppercase tracking-wider text-amber-700">Rejected</p>
            <p className="text-xl sm:text-2xl font-bold text-amber-700 mt-1">{summary.rejected}</p>
          </Card>
          <Card className="p-3 sm:p-4 border-rose-200 bg-rose-50/40" data-testid="public-api-summary-error">
            <p className="text-[10px] sm:text-xs uppercase tracking-wider text-rose-700">Error</p>
            <p className="text-xl sm:text-2xl font-bold text-rose-700 mt-1">{summary.error}</p>
          </Card>
        </div>

        {/* Filters */}
        <Card className="p-4" data-testid="public-api-logs-filters">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1.5 block">Window</label>
              <Select value={String(days)} onValueChange={(v) => setDays(parseInt(v, 10))}>
                <SelectTrigger data-testid="public-api-days-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DAYS_OPTIONS.map((d) => (
                    <SelectItem key={d.value} value={String(d.value)}>{d.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1.5 block">Status</label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger data-testid="public-api-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="success">Success</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                  <SelectItem value="error">Error</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1.5 block">Action</label>
              <Select value={endpointFilter} onValueChange={setEndpointFilter}>
                <SelectTrigger data-testid="public-api-endpoint-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All actions</SelectItem>
                  <SelectItem value="book">Book</SelectItem>
                  <SelectItem value="cancel">Cancel</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1.5 block">Partner</label>
              <Select value={partnerFilter} onValueChange={setPartnerFilter}>
                <SelectTrigger data-testid="public-api-partner-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All partners</SelectItem>
                  {partners.map((p) => (
                    <SelectItem key={p} value={p}>{p}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1.5 block">Search order / mobile</label>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                <Input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="thOrderId / mobile"
                  className="pl-9 pr-9"
                  data-testid="public-api-search-input"
                />
                {search && (
                  <button
                    type="button"
                    onClick={() => setSearch('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                    data-testid="public-api-clear-search"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </Card>

        {/* Table */}
        <Card className="overflow-visible">
          {loading ? (
            <div className="p-12 text-center text-slate-500">Loading logs…</div>
          ) : rows.length === 0 ? (
            <div className="p-12 text-center text-slate-500" data-testid="public-api-logs-empty">
              No API logs match the current filters.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="public-api-logs-table">
                <thead className="bg-slate-900 text-white">
                  <tr>
                    <th className="px-3 py-3 text-left font-semibold w-8" />
                    <th className="px-3 py-3 text-left font-semibold whitespace-nowrap">Received At</th>
                    <th className="px-3 py-3 text-left font-semibold">Action</th>
                    <th className="px-3 py-3 text-left font-semibold">Partner</th>
                    <th className="px-3 py-3 text-left font-semibold whitespace-nowrap">Order ID</th>
                    <th className="px-3 py-3 text-left font-semibold">Player</th>
                    <th className="px-3 py-3 text-left font-semibold whitespace-nowrap">Mobile</th>
                    <th className="px-3 py-3 text-center font-semibold">Slots</th>
                    <th className="px-3 py-3 text-right font-semibold">Amount</th>
                    <th className="px-3 py-3 text-center font-semibold">Status</th>
                    <th className="px-3 py-3 text-left font-semibold">Message</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const meta = STATUS_META[r.status] || STATUS_META.error;
                    const isOpen = expanded === r.id;
                    const slotCount = Array.isArray(r.slots) ? r.slots.length : 0;
                    return (
                      <React.Fragment key={r.id}>
                        <tr
                          className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                          onClick={() => setExpanded(isOpen ? null : r.id)}
                          data-testid={`public-api-log-row-${r.id}`}
                        >
                          <td className="px-3 py-2.5 text-slate-400">
                            {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          </td>
                          <td className="px-3 py-2.5 text-slate-700 whitespace-nowrap">{formatAt(r.created_at)}</td>
                          <td className="px-3 py-2.5">
                            <span className="inline-flex items-center px-2 py-0.5 rounded bg-slate-100 text-slate-700 text-xs font-medium capitalize">
                              {r.endpoint}
                            </span>
                          </td>
                          <td className="px-3 py-2.5 text-slate-900 font-medium">{r.partner_name || '—'}</td>
                          <td className="px-3 py-2.5 text-slate-700 font-mono text-xs whitespace-nowrap">{r.th_order_id || '—'}</td>
                          <td className="px-3 py-2.5 text-slate-700">{r.player_name || '—'}</td>
                          <td className="px-3 py-2.5 text-slate-600 font-mono text-xs whitespace-nowrap">{r.player_mobile || '—'}</td>
                          <td className="px-3 py-2.5 text-center text-slate-700">{slotCount || '—'}</td>
                          <td className="px-3 py-2.5 text-right text-slate-700">{r.amount ? `₹${r.amount}` : '—'}</td>
                          <td className="px-3 py-2.5 text-center">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-medium ${meta.pill}`}>
                              {meta.label}
                              <span className="text-[10px] opacity-70">{r.status_code}</span>
                            </span>
                          </td>
                          <td className="px-3 py-2.5 text-slate-500 text-xs max-w-[260px] truncate" title={r.message}>
                            {r.message || '—'}
                          </td>
                        </tr>
                        {isOpen && (
                          <tr className="bg-slate-50 border-b border-slate-100" data-testid={`public-api-log-detail-${r.id}`}>
                            <td />
                            <td colSpan={10} className="px-3 py-3">
                              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 text-xs">
                                <div>
                                  <p className="font-semibold text-slate-600 mb-1">Request payload</p>
                                  <pre className="bg-white border border-slate-200 rounded p-2 overflow-x-auto text-[11px] text-slate-700">
{JSON.stringify(r.request_body || {}, null, 2)}
                                  </pre>
                                </div>
                                <div>
                                  <p className="font-semibold text-slate-600 mb-1">Response</p>
                                  <pre className="bg-white border border-slate-200 rounded p-2 overflow-x-auto text-[11px] text-slate-700">
{JSON.stringify(r.response || { status: r.status, status_code: r.status_code, message: r.message }, null, 2)}
                                  </pre>
                                  <p className="mt-2 text-[11px] text-slate-400">API key: {r.api_key_masked}</p>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {!loading && rows.length > 0 && (
            <p className="px-3 py-2 text-xs text-slate-400 text-right">
              Showing {rows.length} most recent — refine filters to narrow.
            </p>
          )}
        </Card>
      </div>
    </DashboardLayout>
  );
}
